import { FC, useCallback, useEffect, useState } from 'react';

import { Settings } from 'utils/constants/dynamicSettings';

import Button, { ButtonKind } from 'bloko/blocks/button';
import Column from 'bloko/blocks/column';
import HSpacing from 'bloko/blocks/hSpacing';
import Modal, { ModalContent, ModalFooter, ModalHeader, ModalTitle } from 'bloko/blocks/modal';
import Text from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';

import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import { fetchQuiz } from 'components/QuizModal/fetchQuiz';
import Question from 'components/QuizModal/Question';
import { saveQuiz } from 'components/QuizModal/saveQuiz';

import type { Answers } from 'store/models/quiz';
import { UserType } from 'store/models/user';
import { useDispatch, useSelector } from 'store/utils';

type IsEmptyAnswers = (answers: Answers) => boolean;

const isEmptyAnswers: IsEmptyAnswers = (answers) => {
    const answersKeys = Object.keys(answers);
    return answersKeys.length === 0 || answersKeys.every((key) => answers[key].length === 0);
};

const QuizModal: FC = () => {
    const isEmployer = useSelector((state) => state.user.user?.userType === UserType.Employer);
    const { data, isVisible } = useSelector((state) => state.quiz);

    const dispatch = useDispatch();

    const [step, setStep] = useState(0);
    const [isSaving, setSaving] = useState(false);
    const [answers, setAnswers] = useState<Answers>({});

    const trl = useTrls();

    const settings = useSettings();

    const quizId = data?.id;

    useEffect(() => {
        if (isEmployer && settings(Settings.EnableQuiz) && !data) {
            void dispatch(fetchQuiz());
        }
    }, [data, dispatch, isEmployer, settings]);

    const goPrev = () => setStep(step - 1);
    const goNext = () => setStep(step + 1);

    const onClose = useCallback(() => {
        setAnswers({});
        if (quizId) {
            void dispatch(saveQuiz(quizId, {}, setSaving, true));
        }
    }, [dispatch, quizId]);

    if (!data || !quizId) {
        return null;
    }

    const {
        header,
        quizType: { questions },
    } = data;

    const questionId = questions[step].id;

    const hasPrev = step > 0;
    const hasNext = questions.length - 1 !== step;

    return (
        <Modal visible={isVisible} onClose={onClose}>
            <ModalHeader>
                <ModalTitle>{header}</ModalTitle>
            </ModalHeader>
            <ModalContent>
                <Column xs="4" s="8" m="8" l="8" container>
                    {questions.length > 1 && (
                        <>
                            <Text strong>{trl('quiz.question', [step + 1])}</Text>
                            <VSpacing base={2} />
                        </>
                    )}
                    <Question
                        currentAnswer={answers[questionId] || []}
                        setAnswer={(newAnswer) => setAnswers({ ...answers, [questionId]: newAnswer })}
                        {...questions[step]}
                    />
                </Column>
            </ModalContent>
            <ModalFooter>
                {hasPrev && <Button onClick={goPrev}>{trl('quiz.back')}</Button>}
                <HSpacing base={2} />
                {hasNext ? (
                    <Button kind={ButtonKind.Primary} onClick={goNext}>
                        {trl('quiz.next')}
                    </Button>
                ) : (
                    <Button
                        kind={ButtonKind.Primary}
                        onClick={() => dispatch(saveQuiz(quizId, answers, setSaving))}
                        disabled={isSaving || isEmptyAnswers(answers)}
                    >
                        {trl('quiz.send')}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default QuizModal;
